import {
  adjustmentSessionIdFormat,
  inboundShipmentIdFormat,
  locationIdFormat,
} from "@redotech/redo-ids-cockroach";
import { VALID_PAGE_SIZES } from "@redotech/redo-model/views/views";
import { AdjustmentSessionKind } from "@redotech/redo-prisma-client/enums";
import {
  AdvancedFilterDataSchema,
  FilterGroupSchema,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  OpensearchPaginationCursorSchema,
  SortDirection,
} from "@redotech/redo-views-common/table";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const AdjustmentSessionSearchRowSchema_ = z.object({
  id: adjustmentSessionIdFormat.schema,
  locationId: locationIdFormat.schema,
  locationName: z.string(),
  legacyUserId: z.string(),
  teamMemberName: z.string().nullable(),
  teamMemberEmail: z.string().nullable(),
  kind: createEnum(AdjustmentSessionKind),
  sourceId: z.string().nullable(),
  inboundShipmentId: inboundShipmentIdFormat.schema.nullable(),
  inboundShipmentNumber: z.string().nullable(),
  parentSessionId: adjustmentSessionIdFormat.schema.nullable(),
  notes: z.string().nullish(),
  status: createEnum([
    "in_progress",
    "submitted",
    "completed",
    "cancelled",
    "reversed",
  ]),
  productCount: z.number(),
  totalQuantity: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  submittedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
});

export const AdjustmentSessionSearchRowSchema: z.ZodType<AdjustmentSessionSearchRow> =
  AdjustmentSessionSearchRowSchema_;
export interface AdjustmentSessionSearchRow extends z.infer<
  typeof AdjustmentSessionSearchRowSchema_
> {}

export const adjustmentSessionFilterNames = [
  "locationName",
  "userName",
] as const;
export type AdjustmentSessionFilterName =
  (typeof adjustmentSessionFilterNames)[number];

export const searchAdjustmentSessions = {
  name: "searchAdjustmentSessions" as const,
  input: z.object({
    pageNumber: z.number(),
    pageSize: z
      .number()
      .refine((value) => VALID_PAGE_SIZES.includes(value as any), {
        message: `pageSize must be one of: ${VALID_PAGE_SIZES.join(", ")}`,
      }),
    search: z.string().nullish(),
    sort: z
      .object({ key: z.string(), direction: createEnum(SortDirection) })
      .nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
  output: z.object({
    sessions: z.array(AdjustmentSessionSearchRowSchema),
    opensearchPaginationCursor: OpensearchPaginationCursorSchema.nullish(),
  }),
};

export const searchAdjustmentSessionsCount = {
  name: "searchAdjustmentSessionsCount" as const,
  input: z.object({
    search: z.string().nullish(),
    filterGroup: FilterGroupSchema.nullish(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
  }),
  output: z.object({ total: z.number() }),
};

export const searchAdjustmentSessionsUniqueValues = {
  name: "searchAdjustmentSessionsUniqueValues" as const,
  input: z.object({
    filterName: createEnum(adjustmentSessionFilterNames),
    filterSearch: z.string().nullish(),
  }),
  output: z.object({
    values: z.array(
      z.union([z.string(), z.object({ value: z.string(), label: z.string() })]),
    ),
  }),
};
